import { useState, useCallback, useMemo, ChangeEvent, useRef, useEffect, ReactNode } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Pill } from '@/components/shared/Pill';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { TemplateTypeWithAllTab } from '@/types';
import { ALL_POIS_TAB_LABEL, NOT_APPLICABLE_LABEL, WORKPACK_TYPE_LABELS } from '@/constants';
import {
  AllStructuresForHomePageQuery,
  useAllStructuresForHomePageQuery,
  useGetPointOfInterestGroupedByWorkpackQuery,
  LogTemplateType,
  ShiftType,
  AllWorklogsTemplatesForKpiTrackerQuery,
  useAllPointOfInterestTemplateQuery,
  AllWorklogsTemplatesForKpiTrackerDocument,
} from '@/__generated__/graphql';
import { PageLoader } from '@/components/shared/PageLoader';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { useRouter } from '@/hooks';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useAtom, useSetAtom } from 'jotai';
import { useApolloClient } from '@apollo/client';
import { KpiPanelDrawer } from '@/modules/KpiTracker/components/Panel/KpiPanelDrawer';
import * as kpiState from '@/modules/KpiTracker/state';
import { selectedStructureId as structureIdState } from '@/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { pointOfInterestTabsStyles } from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/styles';
import { updateUrl } from '@/utils';
import { useWorkpackStatusLabels } from '@/hooks/useWorkpackStatusLabels';
import queryString from 'query-string';

const workpackColumns = [
  { label: 'Workpack', width: '20%' },
  { label: 'POIs', width: '8%' },
  { label: 'Type', width: '12%' },
  { label: 'WBS', width: '12%' },
  { label: 'Components', width: '12%' },
  { label: 'Status', width: '16%' },
  { label: 'Action', width: '10%' },
];

const DRAWER_WIDTH = '60vw';

export const LogWorkDrawer = () => {
  const [logWorkDrawerOpen, setLogWorkDrawerOpen] = useAtom(kpiState.kpiPlatformWorkpackDrawerOpen);
  const router = useRouter();
  const structureId = useGetStructureId();
  const { platformId } = router.query;
  const queryParams = queryString.parse(window.location.search, { arrayFormat: 'comma' });
  const assemblyTemplateId = useSelectedAssemblyTemplateId();
  const navbarHeight = useNavbarHeight();
  const apolloClient = useApolloClient();
  const { getWorkpackStatusLabel } = useWorkpackStatusLabels();
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 30 });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentStage, setCurrentStage] = useState<'DISPLAY_WORKPACKS' | 'DISPLAY_WORKLOGS'>(
    'DISPLAY_WORKPACKS'
  );
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [selectedDeploymentFilter, setSelectedDeploymentFilter] = useState<string[]>([]);
  const [activeDeployments, setActiveDeployments] = useState<
    AllStructuresForHomePageQuery['allStructures'] | undefined
  >([]);
  const hasInitializedDeployments = useRef(false);
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [activeDeploymentsWorkLogsTemplates, setActiveDeploymentsWorkLogsTemplates] = useState<
    Record<string, AllWorklogsTemplatesForKpiTrackerQuery | undefined>
  >({});
  const [kpiPanelOpen, setKpiPanelOpen] = useAtom(kpiState.kpiPanelOpen);
  const setWorkpackIdForKpiTracker = useSetAtom(kpiState.selectedWorkpackIdForKpiTracker);
  const setSelectedLogTemplateType = useSetAtom(kpiState.selectedLogTemplateType);
  const setKpiPanelStructureIdOverride = useSetAtom(kpiState.kpiPanelStructureIdOverride);
  const setSelectedDate = useSetAtom(kpiState.selectedDate);
  const setSelectedShift = useSetAtom(kpiState.selectedShift);
  const [selectedPoiTypeFilterIndex, setSelectedPoiTypeFilterIndex] = useState<number>(0);
  const setStructureId = useSetAtom(structureIdState);

  const { data: poiTemplatesData } = useAllPointOfInterestTemplateQuery({
    variables: {
      structureId: activeDeployments?.[0]?.id ?? '',
      assemblyTemplateId: assemblyTemplateId ?? '',
    },
  });

  const allPOIsTemplatesWithAllTab: TemplateTypeWithAllTab[] = useMemo(
    () => [
      {
        name: ALL_POIS_TAB_LABEL,
        type: '',
        fields: poiTemplatesData?.allPointOfInterestTemplate?.[0]?.fields ?? [],
        key: 'all',
      },
      ...(poiTemplatesData?.allPointOfInterestTemplate ?? []),
    ],
    [poiTemplatesData?.allPointOfInterestTemplate]
  );

  const { loading: structuresLoading, data: structuresData } = useAllStructuresForHomePageQuery({
    variables: {
      input: {
        platformId: platformId?.toString(),
      },
    },
    skip: !platformId,
  });

  useEffect(() => {
    const { allStructures } = structuresData || {};

    const active = allStructures?.filter((item) => item.isActive === true);
    setActiveDeployments(active);
  }, [structuresLoading, structuresData]);

  useEffect(() => {
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    searchDebounceTimer.current = setTimeout(() => {
      setActiveSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    if (
      typeof queryParams.structureId === 'string' &&
      queryParams.structureId &&
      typeof queryParams.kpiWorkpackId === 'string' &&
      queryParams.kpiWorkpackId
    ) {
      setStructureId(queryParams.structureId);
      setWorkpackIdForKpiTracker(queryParams.kpiWorkpackId);
      setSelectedLogTemplateType(LogTemplateType.WorklogTemplate);
      setCurrentStage('DISPLAY_WORKLOGS');
      setKpiPanelOpen(true);
    }
  }, []);

  const onClose = () => {
    setKpiPanelOpen(false);
    setLogWorkDrawerOpen(false);
  };

  useEffect(() => {
    // Set all deployments as selected by default only on initial load
    if (activeDeployments && activeDeployments.length > 0 && !hasInitializedDeployments.current) {
      // Fetch worklog templates for all active deployments
      const fetchTemplatesForDeployments = async () => {
        const templatesMap: Record<string, AllWorklogsTemplatesForKpiTrackerQuery | undefined> = {};

        for (const deployment of activeDeployments) {
          try {
            const result = await apolloClient.query({
              query: AllWorklogsTemplatesForKpiTrackerDocument,
              variables: {
                input: {
                  structureId: deployment.id,
                  type: LogTemplateType.WorklogTemplate,
                },
              },
              fetchPolicy: 'cache-first',
            });
            const template = result.data?.allLogsTemplates?.at(0);
            templatesMap[deployment.id] = template;
          } catch (error) {
            console.error(`Failed to fetch template for ${deployment.id}:`, error);
            templatesMap[deployment.id] = undefined;
          }
        }
        setActiveDeploymentsWorkLogsTemplates(templatesMap);
      };

      fetchTemplatesForDeployments();
      setSelectedDeploymentFilter(activeDeployments.map((d) => d.id));
      hasInitializedDeployments.current = true;
    }
  }, [activeDeployments, apolloClient]);

  const { data: workpacksData, loading: workpacksLoading } =
    useGetPointOfInterestGroupedByWorkpackQuery({
      variables: {
        input: {
          structureId: selectedDeploymentFilter[0] || structureId || '',
          additionalStructureIds:
            selectedDeploymentFilter.length > 1 ? selectedDeploymentFilter.slice(1) : undefined,
          searchTerm: activeSearchTerm,
          type:
            selectedPoiTypeFilterIndex === 0
              ? undefined
              : allPOIsTemplatesWithAllTab[selectedPoiTypeFilterIndex]?.key?.toUpperCase(),
        },
        pageInfo: {
          skip: pagination.page * pagination.rowsPerPage,
          limit: pagination.rowsPerPage,
          sortBy: 'createdAt',
          sortDir: 1,
        },
      },
      skip: !logWorkDrawerOpen || selectedDeploymentFilter.length === 0,
    });

  const { workpacks, totalCount } = useMemo(() => {
    const { allWorkpacks, workpacksCount } = workpacksData ?? {};

    const sortedByUnassigned = allWorkpacks
      ? allWorkpacks.slice().sort((a, b) => {
          if (a._id === null) return 1;
          if (b._id === null) return -1;
          return 0;
        })
      : [];

    return {
      workpacks: sortedByUnassigned.filter((wp) => wp._id),
      totalCount: workpacksCount?.count ?? 0,
    };
  }, [workpacksData]);

  const handleChangeRowsPerPage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = +event.target.value;
    setPagination({ page: 0, rowsPerPage: newRowsPerPage });
  }, []);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPagination((previous) => ({ ...previous, page: newPage }));
  }, []);

  const handleWorkpackSelect = useCallback(
    (workpackId: string, structureId: string) => {
      if (!workpackId) return;
      setStructureId(structureId);
      setKpiPanelStructureIdOverride(structureId);
      setWorkpackIdForKpiTracker(workpackId);
      setSelectedLogTemplateType(LogTemplateType.WorklogTemplate);
      setSelectedDate(new Date());
      setSelectedShift(ShiftType.Day);
      setCurrentStage('DISPLAY_WORKLOGS');
      setKpiPanelOpen(true);
      updateUrl('structureId', structureId.toString());
    },
    [
      setKpiPanelOpen,
      setSelectedLogTemplateType,
      setWorkpackIdForKpiTracker,
      setKpiPanelStructureIdOverride,
      setSelectedDate,
      setSelectedShift,
      setStructureId,
      setCurrentStage,
    ]
  );

  useEffect(() => {
    if (!kpiPanelOpen) {
      setCurrentStage('DISPLAY_WORKPACKS');
    }
  }, [kpiPanelOpen]);

  useEffect(() => {
    if (!logWorkDrawerOpen) {
      setCurrentStage('DISPLAY_WORKPACKS');
    }
  }, [logWorkDrawerOpen]);

  const loading = structuresLoading || workpacksLoading;

  let workpacksContent: ReactNode;
  if (loading) {
    workpacksContent = (
      <Box sx={{ mt: 10 }}>
        <PageLoader />
      </Box>
    );
  } else if (!workpacks || workpacks.length === 0) {
    workpacksContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 10 }}>
        <FeedbackCircle text="No workpacks found" />
      </Box>
    );
  } else {
    workpacksContent = (
      <>
        <TableContainer>
          <Table size="small" sx={{ borderSpacing: '0 8px', borderCollapse: 'separate' }}>
            <TableHead>
              <TableRow>
                {workpackColumns.map((column) => (
                  <TableCell key={column.label} sx={{ fontWeight: 600, width: column.width }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {workpacks.map((data) => {
                const workpackId = data?._id ?? '';
                const workpackName = data.workpack?.name ?? '';
                const hasWorkLogsTemplate = Boolean(
                  activeDeploymentsWorkLogsTemplates[
                    data.structureId ?? selectedDeploymentFilter[0] ?? ''
                  ]
                );

                return (
                  <TableRow
                    key={workpackId}
                    sx={{
                      backgroundColor: 'primary.50',
                      '& td:first-of-type': {
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                      },
                      '& td:last-of-type': {
                        borderTopRightRadius: '8px',
                        borderBottomRightRadius: '8px',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{workpackName}</TableCell>
                    <TableCell>{data.pointOfInterestsCount}</TableCell>
                    <TableCell>
                      {data.workpack?.type
                        ? WORKPACK_TYPE_LABELS[data.workpack.type]
                        : NOT_APPLICABLE_LABEL}
                    </TableCell>
                    <TableCell>{data.workpack?.wbs ?? NOT_APPLICABLE_LABEL}</TableCell>
                    <TableCell>{data.assembliesCount}</TableCell>
                    <TableCell>
                      {data.workpack?.status
                        ? getWorkpackStatusLabel(data.workpack.status)
                        : NOT_APPLICABLE_LABEL}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        describeChild
                        title={
                          hasWorkLogsTemplate
                            ? ''
                            : 'No Work Logs Templates Configured for this Deployment'
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              handleWorkpackSelect(
                                workpackId,
                                data.structureId ?? selectedDeploymentFilter[0] ?? ''
                              )
                            }
                            disabled={!hasWorkLogsTemplate}
                          >
                            Select
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          rowsPerPageOptions={[
            { label: '30', value: 30 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onPageChange={handleChangePage}
        />
      </>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={logWorkDrawerOpen}
        onClose={onClose}
        variant="temporary"
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            backgroundColor: 'background.paper',
          },
        }}
      >
        <Stack
          sx={{
            paddingTop: `${navbarHeight}px`,
            height: '100%',
            position: 'relative',
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 500, color: 'text.primary', fontSize: '2rem' }}
              >
                Log Work
              </Typography>
              <Pill label="Select Workpack" state="default" size="medium" disabled />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{ px: 1 }}>
            <BasicTabs
              tabs={allPOIsTemplatesWithAllTab.map((tab, index) => ({
                index,
                label: tab.name,
                component: <></>,
              }))}
              selectedTab={selectedPoiTypeFilterIndex}
              changeTab={(index) => {
                setSelectedPoiTypeFilterIndex(index);
              }}
              variant="pills"
              tabsStyles={pointOfInterestTabsStyles}
            />
          </Box>

          <Typography variant="body1" sx={{ px: 2, mt: 2, color: 'text.secondary' }}>
            Select a workpack to log your work against
          </Typography>

          <Stack direction="row" spacing={2} sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search workpacks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setActiveSearchTerm(searchTerm)}
                      edge="end"
                      size="small"
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              multiple
              size="small"
              options={activeDeployments?.map((d) => d.id) || []}
              disableCloseOnSelect
              loading={structuresLoading}
              value={selectedDeploymentFilter}
              onChange={(_, newValue) => {
                setSelectedDeploymentFilter(newValue);
              }}
              getOptionLabel={(option) => {
                const deployment = activeDeployments?.find((d) => d.id === option);
                return deployment?.name || option;
              }}
              renderOption={(props, option, { selected }) => {
                const deployment = activeDeployments?.find((d) => d.id === option);
                return (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {deployment?.name || option}
                  </li>
                );
              }}
              renderTags={(value) => {
                const count = value.length;
                const total = activeDeployments?.length || 0;
                if (count === 0) return null;
                if (count === total) {
                  return (
                    <Box sx={{ marginLeft: 1 }} component="span">
                      All Deployments ({count})
                    </Box>
                  );
                }
                return (
                  <Box sx={{ marginLeft: 1 }} component="span">
                    {count} Deployment(s) selected
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Deployments"
                  placeholder={selectedDeploymentFilter.length === 0 ? 'Select deployments' : ''}
                />
              )}
              sx={{ minWidth: 300 }}
            />
          </Stack>
          <Box sx={{ p: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>{workpacksContent}</Box>
        </Stack>
      </Drawer>

      {currentStage === 'DISPLAY_WORKLOGS' && <KpiPanelDrawer />}
    </>
  );
};
